import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useFormContext, useWatch } from "react-hook-form";

import makeStyles from "@mui/styles/makeStyles";
import Typography from "@mui/material/Typography";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Slider from "@mui/material/Slider";

import { InputText } from "primereact/inputtext";

import { ValidationMessage } from "../../../shared/ValidationMessage";

const useStyles = makeStyles((theme) => ({
  grid: {
    paddingBottom: theme.spacing(2),
  },
  radioGroup: {
    width: "100%",
  },
  radioItem: {
    flex: 1,
  },
  label: {
    paddingRight: theme.spacing(1),
  },
}));

export function doesRegexMatchValidationBuilderTemplate(regexValue: string) {
  const beginning = "^(\\^)";
  const characterSetStart = "\\[";
  const upperCase = "(A-Z)?";
  const lowerCase = "(a-z)?";
  const digits = "(\\\\d)?";
  const spaces = "(\\\\s)?";
  const specialChars = "(\\\\W)?";
  const characterSetEnd = "\\]";
  const lengthQuantifier = "(({\\d+,?\\d+})|(\\*))";
  const end = "\\$$";

  try {
    const regex = new RegExp(
      beginning +
        characterSetStart +
        upperCase +
        lowerCase +
        digits +
        spaces +
        specialChars +
        characterSetEnd +
        lengthQuantifier +
        end,
    );

    return regex.test(regexValue);
  } catch {
    return false;
  }
}

export function doesRegexContainMinMaxQuantifier(regexValue: string) {
  const regex = new RegExp("{\\d+,?\\d+}");

  return regex.test(regexValue);
}

function isExpressionValid(regexValue: string) {
  try {
    const r = new RegExp(regexValue);

    return Boolean(r);
  } catch {
    return false;
  }
}

const maximumAllowedTextLength = 200;

function getMinMaxRangeFromRegex(regex: string) {
  if (doesRegexContainMinMaxQuantifier(regex)) {
    try {
      const quantifier = regex.substring(regex.indexOf("{") + 1, regex.indexOf("}"));

      return quantifier.split(",").map((value) => Number(value));
    } catch {
      return [0, maximumAllowedTextLength];
    }
  } else return [0, maximumAllowedTextLength];
}

function getInitialPatternValue(regex: string | undefined, pattern: string) {
  return regex?.includes(pattern) ? pattern : "";
}

type ValidationType = "none" | "builder" | "manual";

export function TextValidationBuilder() {
  const { t } = useTranslation();
  const classes = useStyles();

  const {
    control,
    register,
    formState: { errors },
    setValue,
  } = useFormContext();
  const regexValue = useWatch({ control, name: "validation" });

  const [upperCaseRegex, setUpperCaseRegex] = useState(getInitialPatternValue(regexValue, "A-Z"));
  const [lowerCaseRegex, setLowerCaseRegex] = useState(getInitialPatternValue(regexValue, "a-z"));
  const [digitRegex, setDigitRegex] = useState(getInitialPatternValue(regexValue, "\\d"));
  const [spacesRegex, setSpacesRegex] = useState(getInitialPatternValue(regexValue, "\\s"));
  const [specialCharsRegex, setspecialCharsRegex] = useState(getInitialPatternValue(regexValue, "\\W"));

  const [limitingTextLength, setLimitingTextLength] = useState(doesRegexContainMinMaxQuantifier(regexValue));
  const [textLengthRange, setTextLengthRange] = React.useState<number[]>(getMinMaxRangeFromRegex(regexValue));

  const [validationType, setValidationType] = useState<ValidationType>(
    !regexValue ? "none" : doesRegexMatchValidationBuilderTemplate(regexValue) ? "builder" : "manual",
  );

  useEffect(() => {
    if (validationType === "none") {
      setValue("validation", "");
    } else if (validationType === "builder") {
      const lengthQuantifierTemplate = limitingTextLength ? `{${textLengthRange[0]},${textLengthRange[1]}}` : "*";
      const validationBuilderTemplate = `^[${upperCaseRegex}${lowerCaseRegex}${digitRegex}${spacesRegex}${specialCharsRegex}]${lengthQuantifierTemplate}$`;

      setValue("validation", validationBuilderTemplate);
    }
  }, [
    validationType,
    setValue,
    upperCaseRegex,
    lowerCaseRegex,
    digitRegex,
    spacesRegex,
    specialCharsRegex,
    limitingTextLength,
    textLengthRange,
  ]);

  const handleValidationTypeChange = (event: any) => {
    setValidationType(event.target.value);
  };

  function handleAllowUpperCaseCheckboxChange(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) {
    if (checked) setUpperCaseRegex("A-Z");
    else setUpperCaseRegex("");
  }

  function handleAllowLowerCaseCheckboxChange(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) {
    if (checked) setLowerCaseRegex("a-z");
    else setLowerCaseRegex("");
  }

  function handleAllowDigitsCheckboxChange(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) {
    if (checked) setDigitRegex("\\d");
    else setDigitRegex("");
  }

  function handleAllowSpacesCheckboxChange(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) {
    if (checked) setSpacesRegex("\\s");
    else setSpacesRegex("");
  }

  function handleAllowSpecialCharactersCheckboxChange(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) {
    if (checked) setspecialCharsRegex("\\W");
    else setspecialCharsRegex("");
  }

  function handleLimitTextLengthChange(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) {
    setLimitingTextLength(checked);
  }

  function handleTextLengthChange(e: Event, newValue: number | number[]) {
    setTextLengthRange(newValue as number[]);
  }

  function CheckboxItem(props: {
    label: string;
    checked: boolean;
    onChange: (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  }) {
    return (
      <ListItem dense>
        <ListItemIcon>
          <Checkbox
            checked={props.checked}
            onChange={props.onChange}
            color="primary"
            disabled={validationType !== "builder"}
          />
        </ListItemIcon>
        <ListItemText primary={props.label} />
      </ListItem>
    );
  }

  return (
    <>
      <RadioGroup value={validationType} onChange={handleValidationTypeChange} className={classes.radioGroup}>
        <FormControlLabel
          value="none"
          control={<Radio color="primary" />}
          label={t("customQuestions.noValidation")}
          className={classes.radioItem}
        />

        <FormControlLabel
          value="builder"
          control={<Radio color="primary" />}
          label={t("customQuestions.validationBuilder")}
          className={classes.radioItem}
        />

        <Grid container spacing={3} className={classes.grid}>
          <Grid item xs={12} sm={5}>
            <CheckboxItem
              checked={Boolean(upperCaseRegex)}
              onChange={handleAllowUpperCaseCheckboxChange}
              label={t("customQuestions.allowUpperCase")}
            />
            <CheckboxItem
              checked={Boolean(lowerCaseRegex)}
              onChange={handleAllowLowerCaseCheckboxChange}
              label={t("customQuestions.allowLowerCase")}
            />
            <CheckboxItem
              checked={Boolean(digitRegex)}
              onChange={handleAllowDigitsCheckboxChange}
              label={t("customQuestions.allowNumbers")}
            />
            <CheckboxItem
              checked={Boolean(spacesRegex)}
              onChange={handleAllowSpacesCheckboxChange}
              label={t("customQuestions.allowSpaces")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CheckboxItem
              checked={Boolean(specialCharsRegex)}
              onChange={handleAllowSpecialCharactersCheckboxChange}
              label={t("customQuestions.allowSpecialCharacters")}
            />
            <CheckboxItem
              checked={Boolean(limitingTextLength)}
              onChange={handleLimitTextLengthChange}
              label={t("customQuestions.limitLength")}
            />

            <ListItem dense>
              <ListItemIcon />
              <Slider
                value={textLengthRange}
                onChange={handleTextLengthChange}
                valueLabelDisplay="auto"
                max={maximumAllowedTextLength}
                disabled={validationType !== "builder" || !limitingTextLength}
                marks={[
                  { value: 0, label: 0 },
                  {
                    value: maximumAllowedTextLength,
                    label: maximumAllowedTextLength,
                  },
                ]}
              />
            </ListItem>
          </Grid>
        </Grid>

        <FormControlLabel
          value="manual"
          control={<Radio color="primary" />}
          label={t("customQuestions.manualValidation")}
          className={classes.radioItem}
        />

        <ListItem>
          <Typography variant="body2" className={classes.label}>
            {t("customQuestions.expression")}:{" "}
          </Typography>
          <div>
            <InputText
              disabled={validationType !== "manual"}
              {...register("validation", {
                validate: { isValidRegex: (value) => isExpressionValid(value) },
              })}
              maxLength={30}
            />
            {errors.validation && (
              <ValidationMessage
                invalidMessage={t("customQuestions.invalidExpression")}
                validationType="error"
                showIcon
              />
            )}
          </div>
        </ListItem>
      </RadioGroup>
    </>
  );
}
